// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Rematch All Dialog for I-9 documents
 *
 * Re-scores every eligible document in the Partial Match tab against the current HR
 * matching rules — no re-OCR. Each document is re-scored in its own processor run and
 * the Optix write-back fires only where the outcome changed. Documents already in
 * flight (or shredded/purged) are skipped by the server. Unlike Re-run all this takes
 * no options, so it's a plain confirmation.
 */
import type { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
} from '@/components/ui'
import { Calculator } from 'lucide-react'

interface RematchAllDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  documentCount: number | null
  isRematching: boolean
  onConfirm: () => void
}

export const RematchAllDialog: FC<RematchAllDialogProps> = ({
  open,
  onOpenChange,
  documentCount,
  isRematching,
  onConfirm,
}) => {
  const countLabel =
    documentCount !== null
      ? `${documentCount.toLocaleString()} ${documentCount === 1 ? 'document' : 'documents'}`
      : 'all documents'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Rematch all — Partial Match
          </DialogTitle>
          <DialogDescription>
            Re-score {countLabel} in this tab against the current matching rules, with no
            re-OCR. A document whose outcome improves is re-bucketed and its Optix metadata
            re-pushed. Documents already being processed are skipped automatically.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isRematching}>
            <Calculator className="h-4 w-4 mr-1" />
            Rematch all
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
